/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { RefetchOptions, useQuery } from '@tanstack/react-query'
import rest from '../../../client/js/lib/rest'

export type GlobalSettings = {
  CONTACT_PHOTO_FILE_MAX_SIZE: number;
  CONTACT_PHOTO_FILE_TYPES: string[];
  CURRENCY: string;
  ESTATE_TEMPLATE_FILE_TYPES: string[];
  ESTATE_TEMPLATE_MAX_UPLOAD_SIZE: number;
  INVOICE_FILE_TYPES: string[];
  INVOICE_MAX_UPLOAD_SIZE: number;
  INVOICE_TEMPLATE_FILE_TYPES: string[];
  INVOICE_TEMPLATE_MAX_UPLOAD_SIZE: number;
  LEASE_FILE_TYPES: string[];
  LEASE_MAX_UPLOAD_SIZE: number;
  LEASE_TEMPLATE_FILE_TYPES: string[];
  LEASE_TEMPLATE_MAX_UPLOAD_SIZE: number;
  MANAGEMENT_REPORT_TEMPLATE_FILE_TYPES: string[];
  MANAGEMENT_REPORT_TEMPLATE_MAX_UPLOAD_SIZE: number;
  MANDATE_FILE_TYPES: string[];
  MANDATE_MAX_UPLOAD_SIZE: number;
  PHOTO_FILE_TYPES: string[];
  PHOTO_MAX_UPLOAD_SIZE: number;
  TRANSACTION_FILE_TYPES: string[];
  TRANSACTION_MAX_UPLOAD_SIZE: number;
  NODE_ENV: number;
  TIMEZONE: number;
};

export type SettingsContext_ = {
  loading: boolean;
  refetch: (options?: RefetchOptions) => Promise<any>;
  settings: GlobalSettings;
}

const SettingsContext = createContext<SettingsContext_ | null>(null)

export function useSettingsContext () {
  return useContext(SettingsContext)
}

export type SettingsProviderProps = PropsWithChildren

function SettingsProvider (props: SettingsProviderProps) {
  const { children } = props

  const query = useQuery({
    queryKey: ['globalSettings'],
    queryFn: () => rest.get('/api/settings/global').then((r) => r.json())
  })

  const context = useMemo(() => ({
    loading: query.isLoading,
    refetch: query.refetch,
    settings: query.data || {}
  }), [query.data, query.isLoading, query.refetch])

  return <SettingsContext.Provider value={context}>
    {children}
  </SettingsContext.Provider>
}

export default SettingsProvider
